export type PermissionAction = "read" | "write" | "delete" | "configure" | "approve";

/** Column order and short labels for the role editor. */
export const PERMISSION_ACTIONS: Array<{ action: PermissionAction; label: string }> = [
  { action: "read", label: "View" },
  { action: "write", label: "Edit" },
  { action: "delete", label: "Delete" },
  { action: "configure", label: "Configure" },
  { action: "approve", label: "Approve" },
];

export type RbacResourceDefinition = {
  resource: string;
  label: string;
  description: string;
  /**
   * Only the actions the server checks for this resource, each with what it allows. Keep this in
   * sync with the checkPermission(resource, action) calls in routes/ and services/.
   */
  actions: Partial<Record<PermissionAction, string>>;
};

export type RbacCategory = {
  id: string;
  label: string;
  resources: RbacResourceDefinition[];
};

// RBAC resources used by `/api/rbac/*` endpoints and the role editor.
export const RBAC_CATALOG: RbacCategory[] = [
  {
    id: "clients",
    label: "Clients",
    resources: [
      {
        resource: "tenantManagement",
        label: "Tenants",
        description: "Client deployments and their TeleOpd modules.",
        actions: {
          read: "See tenants, their health and their modules.",
          write: "Add and edit tenants and their TeleOpd modules.",
          delete: "Delete tenants.",
          configure: "Change platform settings and move a tenant between Beta and Locked.",
        },
      },
      {
        resource: "billing",
        label: "Billing",
        description: "Invoices, payments and the TeleOpd lock.",
        actions: {
          read: "See billing for every client.",
          write: "Edit charges, invoices and billing rules, and send reminders.",
          configure: "Connect TeleOpd and manage its link.",
          approve: "Record payments, void invoices, extend due dates and pause enforcement.",
        },
      },
    ],
  },
  {
    id: "access",
    label: "Access",
    resources: [
      {
        resource: "userManagement",
        label: "Users",
        description: "SAP admin accounts.",
        actions: {
          read: "See users.",
          write: "Edit users. Adding a user also needs Approve.",
          delete: "Delete users.",
          configure: "Reset passwords.",
          approve: "Add users and suspend or reactivate them.",
        },
      },
      {
        resource: "roleManagement",
        label: "Roles",
        description: "What each role may do.",
        actions: {
          read: "See roles.",
          write: "Create and edit roles.",
          delete: "Delete roles.",
        },
      },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    resources: [
      {
        resource: "auditLogs",
        label: "Audit log",
        description: "The tamper-evident record of every change.",
        actions: { read: "See the audit log." },
      },
      {
        resource: "analytics",
        label: "Analytics",
        description: "Platform-wide usage trends.",
        actions: { read: "See analytics." },
      },
      {
        resource: "notificationTemplates",
        label: "Notification templates",
        description: "Email and message templates.",
        actions: {
          read: "See templates.",
          write: "Create and edit templates.",
        },
      },
      {
        resource: "moduleManagement",
        label: "Modules (legacy)",
        description: "The old module catalog, being retired.",
        actions: {
          read: "See the module catalog.",
          write: "Edit modules and screens.",
          configure: "Change global module settings.",
        },
      },
    ],
  },
];

export const RBAC_RESOURCES: RbacResourceDefinition[] = RBAC_CATALOG.flatMap(
  (category) => category.resources
);
